/**
 * RuleAuthoringForm — the in-product advisory→canonical trust bar (RFC-0003 D3 / ADR-0010
 * Decision 8). When a reviewer accepts a discovered signal AS canonical, they author and SIGN a
 * curated obligation here: an official https source + its SHA-256 + structured 4a applicability +
 * a legal reference + their reviewer identity. The discovered signal only pre-fills DRAFT values
 * the human must confirm — nothing reaches the runtime store without an explicit submit, and the
 * `PromotedRuleSchema` (https + 64-hex + reviewer email) is validated here before the API re-checks
 * it. This is NOT a legal determination; it is a reviewer-signed, advisory readiness obligation.
 */

import { useState } from "react";
import {
  LegalEffect,
  ObligationKind,
  ObligationTiming,
  PromotedRuleSchema,
  RegulatoryDomain,
  type PromotedRule,
  type Wp29Signal,
} from "@regpulse/contracts";

interface Props {
  signal: Wp29Signal;
  reviewer: string;
  onSubmit: (rule: PromotedRule) => Promise<void>;
  onCancel: () => void;
  submitting?: boolean;
}

/** Slug a title into the `LP-<slug>` id namespace (regex `^LP-[A-Za-z0-9-]+$`). */
function slugId(title: string, fallback: string): string {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-+)|(-+$)/g, "")
    .slice(0, 48);
  return `LP-${slug || fallback.replace(/[^A-Za-z0-9]+/g, "-")}`;
}

const fieldStyle: React.CSSProperties = {
  width: "100%",
  fontSize: 12,
  padding: "var(--space-4) var(--space-8)",
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius-4)",
  background: "var(--color-surface)",
  color: "var(--color-text)",
  boxSizing: "border-box",
};
const labelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  color: "var(--color-text-secondary)",
  display: "block",
  marginBottom: 2,
};

function Field({ name, label, value, onChange }: { name: string; label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label style={{ display: "block" }}>
      <span style={labelStyle}>{label}</span>
      <input
        data-testid={`rule-field-${name}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={fieldStyle}
      />
    </label>
  );
}

function Select({ name, label, value, options, onChange }: { name: string; label: string; value: string; options: readonly string[]; onChange: (v: string) => void }) {
  return (
    <label style={{ display: "block" }}>
      <span style={labelStyle}>{label}</span>
      <select data-testid={`rule-field-${name}`} value={value} onChange={(e) => onChange(e.target.value)} style={fieldStyle}>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

export function RuleAuthoringForm({ signal, reviewer, onSubmit, onCancel, submitting = false }: Props) {
  const [id, setId] = useState(() => slugId(signal.title, signal.id));
  const [title, setTitle] = useState(signal.title);
  const [requirementText, setRequirementText] = useState(signal.summary);
  const [instrument, setInstrument] = useState(signal.instrument);
  const [article, setArticle] = useState(signal.article ?? "");
  const [officialSourceUrl, setOfficialSourceUrl] = useState(signal.sourceUrl);
  const [sourceSha256, setSourceSha256] = useState(signal.sha256);
  const [regulatoryDomain, setRegulatoryDomain] = useState<string>("Cybersecurity");
  const [obligationKind, setObligationKind] = useState<string>("hard_law");
  const [obligationTiming, setObligationTiming] = useState<string>("pre_market");
  const [legalEffect, setLegalEffect] = useState<string>("market_access_blocker");
  const [jurisdiction, setJurisdiction] = useState("EU");
  const [jurisdictions, setJurisdictions] = useState("EU");
  const [configField, setConfigField] = useState("");
  const [configEquals, setConfigEquals] = useState("");
  const [reviewerEmail, setReviewerEmail] = useState(reviewer);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    // configField + configEquals are an all-or-nothing pair (the contract superRefine enforces
    // both-or-neither). A filled field with a blank value would otherwise build a broken
    // `field == ""` condition — block it with clear feedback instead.
    if (configField.trim() && !configEquals.trim()) {
      setError("Enter a value for configEquals, or clear the config field.");
      return;
    }
    const jur = jurisdictions
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const applicability: PromotedRule["applicability"] = { mode: "jurisdiction_gated", jurisdictions: jur };
    if (configField.trim()) {
      applicability.configField = configField.trim();
      // "true"/"false" → boolean; anything else → string equality.
      applicability.configEquals =
        configEquals.trim() === "true" ? true : configEquals.trim() === "false" ? false : configEquals.trim();
    }
    const candidate = {
      id: id.trim(),
      title: title.trim(),
      regulatoryDomain,
      jurisdiction: jurisdiction.trim(),
      obligationKind,
      obligationTiming,
      legalEffect,
      requirementText: requirementText.trim(),
      legalReference: article.trim()
        ? { instrument: instrument.trim(), article: article.trim() }
        : { instrument: instrument.trim() },
      officialSourceUrl: officialSourceUrl.trim(),
      sourceSha256: sourceSha256.trim(),
      applicability,
      reviewer: reviewerEmail.trim(),
      promotedAt: new Date().toISOString(),
      originatingSignalId: signal.id,
    };
    const parsed = PromotedRuleSchema.safeParse(candidate);
    if (!parsed.success) {
      const first = parsed.error.issues[0];
      setError(
        first ? `${first.path.join(".") || "rule"}: ${first.message}` : "The authored rule is incomplete or invalid.",
      );
      return;
    }
    try {
      await onSubmit(parsed.data);
    } catch (err) {
      setError(`Submit failed: ${(err as Error).message ?? "unknown error"}`);
    }
  };

  return (
    <div
      data-testid="rule-authoring-form"
      style={{
        marginTop: "var(--space-8)",
        padding: "var(--space-12)",
        border: "1px solid var(--ft-hard-law, var(--color-border-strong))",
        borderRadius: "var(--radius-8)",
        background: "var(--color-surface-sunken)",
        display: "grid",
        gap: "var(--space-8)",
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 700 }}>Author canonical obligation</div>
      <p style={{ margin: 0, fontSize: 10, color: "var(--color-text-secondary)", lineHeight: 1.4 }}>
        You are signing a reviewer-authored obligation from this discovered lead. Confirm the official
        source + hash and complete the classification. Advisory readiness only — not a legal determination
        (ADR-0002). It is materialised into a local, non-declared-scope rule you can revert.
      </p>

      <Field name="id" label="Rule id (LP-…)" value={id} onChange={setId} />
      <Field name="title" label="Title" value={title} onChange={setTitle} />
      <label style={{ display: "block" }}>
        <span style={labelStyle}>Requirement text</span>
        <textarea
          data-testid="rule-field-requirementText"
          value={requirementText}
          onChange={(e) => setRequirementText(e.target.value)}
          rows={2}
          style={{ ...fieldStyle, resize: "vertical" }}
        />
      </label>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-8)" }}>
        <Field name="instrument" label="Legal instrument" value={instrument} onChange={setInstrument} />
        <Field name="article" label="Article / annex (optional)" value={article} onChange={setArticle} />
      </div>
      <Field name="officialSourceUrl" label="Official source URL (https)" value={officialSourceUrl} onChange={setOfficialSourceUrl} />
      <Field name="sourceSha256" label="Source SHA-256 (64-hex)" value={sourceSha256} onChange={setSourceSha256} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-8)" }}>
        <Select name="regulatoryDomain" label="Domain" value={regulatoryDomain} options={RegulatoryDomain.options} onChange={setRegulatoryDomain} />
        <Select name="obligationKind" label="Obligation kind" value={obligationKind} options={ObligationKind.options} onChange={setObligationKind} />
        <Select name="obligationTiming" label="Timing" value={obligationTiming} options={ObligationTiming.options} onChange={setObligationTiming} />
        <Select name="legalEffect" label="Legal effect" value={legalEffect} options={LegalEffect.options} onChange={setLegalEffect} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-8)" }}>
        <Field name="jurisdiction" label="Jurisdiction (card)" value={jurisdiction} onChange={setJurisdiction} />
        <Field name="jurisdictions" label="Applies in markets (comma-sep)" value={jurisdictions} onChange={setJurisdictions} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-8)" }}>
        <Field name="configField" label="Config field (optional)" value={configField} onChange={setConfigField} />
        <Field name="configEquals" label="equals (true/false/value)" value={configEquals} onChange={setConfigEquals} />
      </div>
      <Field name="reviewer" label="Reviewer (email)" value={reviewerEmail} onChange={setReviewerEmail} />

      {error && (
        <div
          data-testid="rule-authoring-error"
          role="alert"
          style={{
            fontSize: 11,
            color: "var(--ft-hard-law, var(--color-danger, #b00))",
            background: "var(--ft-hard-law-soft, var(--color-surface))",
            border: "1px dashed var(--ft-hard-law, var(--color-border-strong))",
            borderRadius: "var(--radius-4)",
            padding: "var(--space-4) var(--space-8)",
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: "flex", gap: "var(--space-8)" }}>
        <button
          type="button"
          data-testid="rule-authoring-submit"
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            fontSize: 11,
            fontWeight: 700,
            padding: "var(--space-4) var(--space-12)",
            borderRadius: "var(--radius-4)",
            border: "1px solid var(--ft-hard-law, var(--color-border-strong))",
            background: "var(--ft-hard-law-soft, var(--color-surface))",
            color: "var(--ft-hard-law, var(--color-text))",
            cursor: submitting ? "default" : "pointer",
          }}
        >
          {submitting ? "signing…" : "Sign & promote → canonical"}
        </button>
        <button
          type="button"
          data-testid="rule-authoring-cancel"
          onClick={onCancel}
          disabled={submitting}
          style={{
            fontSize: 11,
            padding: "var(--space-4) var(--space-12)",
            borderRadius: "var(--radius-4)",
            border: "1px solid var(--color-border)",
            background: "var(--color-surface)",
            color: "var(--color-text-secondary)",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
